import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./HomeCarousel.css";

const SLIDES = [
  {
    id: "social",
    category: "Social",
    accent: "#c084fc",
    bg: "",
    glow: "",
    question: "Growing on social media?",
    tagline: "Write where you're headed. Document every post, reel, and deal.",
    node: {
      category: "Social",
      text: "100K followers on Instagram, brand deals, and a product of my own.",
      updates: ["First viral reel — 2.1M views", "Brand deal signed. First income from content"],
    },
  },
  {
    id: "fitness",
    category: "Health",
    accent: "#4ade80",
    bg: "",
    glow: "",
    question: "Working on your fitness?",
    tagline: "Write what you want to become. Document the journey as it unfolds.",
    node: {
      category: "Health",
      text: "Running a sub-4-hour marathon before the year is out.",
      updates: ["Hit 15km long run — new personal best", "Down 8kg since I started logging"],
    },
  },
  {
    id: "startup",
    category: "Business",
    accent: "#f17501",
    bg: "",
    glow: "",
    question: "Building a startup?",
    tagline: "Write what you're building. Document every launch, customer, and win.",
    node: {
      category: "Business",
      text: "I'm building a SaaS that reaches $10K MRR by end of year.",
      updates: ["Closed first paying customer — $99/mo", "Launched v1. 47 signups in 24h"],
    },
  },
  {
    id: "career",
    category: "Career",
    accent: "#22d3ee",
    bg: "",
    glow: "",
    question: "Switching careers?",
    tagline: "Write the move you're making. Document every step as it unfolds.",
    node: {
      category: "Career",
      text: "Leaving finance. Landing a product role at a company I actually believe in.",
      updates: ["Shipped first side project — 200 users", "First PM interview scheduled — Google"],
    },
  },
  {
    id: "finance",
    category: "Finance",
    accent: "#34d399",
    bg: "",
    glow: "",
    question: "Building toward a number?",
    tagline: "Write the future you're building. Document every investment and income stream.",
    node: {
      category: "Finance",
      text: "€100K net worth and a passive income stream before I turn 30.",
      updates: ["Investment portfolio crossed €20K", "Side income: €800 last month"],
    },
  },
  {
    id: "writing",
    category: "Learning",
    accent: "#fbbf24",
    bg: "",
    glow: "",
    question: "Writing your first book?",
    tagline: "Write the vision of finishing it. Document every chapter as you go.",
    node: {
      category: "Learning",
      text: "First draft of my novel done before the year ends. No more waiting.",
      updates: ["Chapter 7 done. Crossed 30,000 words", "Found the ending — writing backwards now"],
    },
  },
];

const AUTO_PLAY_MS = 4200;

const HomeCarousel = () => {
  const [active, setActive] = useState(0);
  const [animDir, setAnimDir] = useState("next");
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  const go = useCallback((idx, dir = "next") => {
    setAnimDir(dir);
    setActive(idx);
  }, []);

  const next = useCallback(() => {
    go((active + 1) % SLIDES.length, "next");
  }, [active, go]);

  const prev = useCallback(() => {
    go((active - 1 + SLIDES.length) % SLIDES.length, "prev");
  }, [active, go]);

  useEffect(() => {
    timerRef.current = setInterval(next, AUTO_PLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, AUTO_PLAY_MS);
  };

  const slide = SLIDES[active];

  return (
    <section className="hc-section">
      <div className="hc-header">
        <h2>
          Works for<br />
          <span className="accent">whatever you're building.</span>
        </h2>
      </div>

      <div
        className="hc-stage"
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 44) { diff > 0 ? next() : prev(); resetTimer(); }
          touchStartX.current = null;
        }}
      >
        <button
          className="hc-arrow hc-arrow-prev"
          onClick={() => { prev(); resetTimer(); }}
          aria-label="Previous"
        >
          ‹
        </button>

        <div
          className={`hc-card hc-card-${animDir}`}
          key={slide.id}
          style={{ "--accent": slide.accent }}
        >
          <div className="hc-card-left">
            <span className="hc-cat" style={{ color: slide.accent, borderColor: slide.accent }}>
              {slide.category}
            </span>
            <h3 className="hc-question">{slide.question}</h3>
            <p className="hc-tagline">{slide.tagline}</p>
            <Link to="/start" className="hc-cta" style={{ background: slide.accent }}>
              Start your record →
            </Link>
          </div>

          <div className="hc-card-right">
            <div className="hc-mockup">
              <div className="hc-mockup-bar">
                <span className="hc-mockup-dot" />
                <span className="hc-mockup-dot" />
                <span className="hc-mockup-dot" />
                <span className="hc-mockup-title">dmyor record</span>
              </div>
              <div className="hc-mockup-body">
                <span className="hc-mockup-cat" style={{ color: slide.accent }}>
                  {slide.node.category}
                </span>
                <p className="hc-mockup-text">{slide.node.text}</p>
                <div className="hc-mockup-updates">
                  <p className="hc-mockup-updates-label">Updates</p>
                  {slide.node.updates.map((u, i) => (
                    <div className="hc-mockup-update" key={i}>
                      <span className="hc-mockup-update-dot" style={{ background: slide.accent }} />
                      <span>{u}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          className="hc-arrow hc-arrow-next"
          onClick={() => { next(); resetTimer(); }}
          aria-label="Next"
        >
          ›
        </button>
      </div>

      <div className="hc-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            className={`hc-dot ${i === active ? "active" : ""}`}
            onClick={() => { go(i, i > active ? "next" : "prev"); resetTimer(); }}
            aria-label={`Go to slide ${i + 1}`}
            style={i === active ? { background: "#111", borderColor: "#111" } : {}}
          />
        ))}
      </div>
    </section>
  );
};

export default HomeCarousel;
